import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import {
  WhitePenciledLogo,
  StyledPenciledTextLogo
} from "../../styles/HeaderStyles";
import { DetailsSection } from "./DetailsSection";
import { CalendarSection } from "./CalendarSection";
import { SlotsSection } from "./SlotsSection";
import { CalendarOff, CircleAlertIcon, CircleCheckIcon } from "lucide-react";
import { InitialVisitBooking, useInitialVisitDetails } from "../../hooks/useInitialVisitDetails";
import { useInitialVisitAvailability } from "../../hooks/useInitialVisitAvailability";
import { useInitialVisitStore } from "../../stores/initialVisitStore";
import { useEffect } from "react";
import { useTheme } from "../../components/ui/theme-provider";
import { BookingConfirmation } from "./BookingConfirmation";
import { WaitlistSelection } from "./WaitlistSelection";
import { posthog } from "posthog-js";
import { convertE164ToReadable } from "../../utils/utils";

export default function BookInitialVisit() {
  const { uniqueId } = useParams<{ uniqueId: string }>();

  const { resolvedTheme } = useTheme();

  const { setUniqueId, selectedSlot, bookingStatus, waitlist } =
    useInitialVisitStore();

  useEffect(() => {
    if (uniqueId) {
      setUniqueId(uniqueId);
    }
  }, [uniqueId, setUniqueId]);

  const { data, error } = useInitialVisitDetails({
    uniqueId,
    enabled: Boolean(uniqueId)
  });

  const { error: availabilityError } = useInitialVisitAvailability({
    uniqueId,
    enabled: Boolean(uniqueId)
  });

  useEffect(() => {
    if (data) {
      posthog?.identify(data.patient.userId);
    }
  }, [data]);

  if (error || availabilityError) {
    return (
      <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
        <Card>
          <CardContent>
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center justify-center size-12 rounded-full bg-destructive/10 mb-8">
                <CircleAlertIcon className="size-6 text-destructive stroke-[1.75]" />
              </div>
              <h1 className="header-2 mb-4 text-center">
                {error?.message || availabilityError?.message}
              </h1>
              <p className="text-base text-muted-foreground text-center">
                Please request a new link via text or contact the clinic for
                help.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (data && (data as InitialVisitBooking).agentActive === false) {
    return (
      <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
        <Card>
          <CardContent>
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center justify-center size-12 rounded-full bg-muted mb-8">
                <CalendarOff className="size-6 text-muted-foreground stroke-[1.75]" />
              </div>
              <h1 className="header-2 mb-4 text-center">
                Booking Unavailable
              </h1>
              <p className="text-base text-muted-foreground text-center max-w-md">
                Online booking through this link isn't available right now.
                Please contact the clinic at {convertE164ToReadable((data as InitialVisitBooking)?.organization.phoneNumber || "")} to schedule your appointment.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    )
  }

  if ((data as InitialVisitBooking)?.patient.archived) {
    return (
      <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
        <Card>
          <CardContent>
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center justify-center size-12 rounded-full bg-blue-500/10 mb-8">
                <CircleAlertIcon className="size-6 text-blue-500 stroke-[1.75]" />
              </div>
              <h1 className="header-2 mb-4 text-center">
                Please Contact the Clinic
              </h1>
              <p className="text-base text-muted-foreground text-center max-w-md">
                Online scheduling is currently unavailable for your account.
                Please contact the clinic at {convertE164ToReadable((data as InitialVisitBooking)?.organization.phoneNumber || "")} for assistance.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    )
  }

  if (bookingStatus === "waitlist") {
    return (
      <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
        <Card>
          <CardContent>
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center justify-center size-12 rounded-full bg-success/10 mb-8">
                <CircleCheckIcon className="size-6 text-success stroke-[1.75]" />
              </div>
              <h1 className="header-2 mb-4 text-center">
                You've Joined the Waitlist!
              </h1>
              <p className="text-base text-muted-foreground text-center max-w-lg">
                We'll text you if a spot opens up. You can update your
                availability by texting us anytime.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (waitlist) {
    return <WaitlistSelection />;
  }

  if (bookingStatus === "confirmed") {
    return (
      <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
        <Card>
          <CardContent>
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center justify-center size-12 rounded-full bg-success/10 mb-8">
                <CircleCheckIcon className="size-6 text-success stroke-[1.75]" />
              </div>
              <h1 className="header-2 mb-4 text-center">
                Appointment Confirmed!
              </h1>
              <p className="text-base text-muted-foreground text-center max-w-lg">
                Your appointment has been successfully scheduled. You will
                receive a confirmation text shortly.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (selectedSlot) {
    return <BookingConfirmation />;
  }

  return (
    <main className="lg:p-8 min-h-svh bg-background lg:bg-accent dark:bg-background flex flex-col items-center justify-center">
      <Card className="p-0 border-0 shadow-none bg-transparent lg:border lg:shadow-sm lg:bg-card w-full lg:w-auto">
        <CardContent className="p-0">
          <div className="flex flex-col lg:flex-row">
            <DetailsSection uniqueId={uniqueId} />
            <CalendarSection />
            <SlotsSection />
          </div>
        </CardContent>
      </Card>
      <Link
        to="https://penciled.com"
        target="_blank"
        rel="noreferrer"
        className="text-xs text-muted-foreground py-6"
      >
        {resolvedTheme === "light" ? (
          <StyledPenciledTextLogo className="!w-28 hidden lg:block" />
        ) : (
          <WhitePenciledLogo className="!w-28 hidden lg:block" />
        )}
      </Link>
    </main>
  );
}
